import React, { useState, createContext, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, 
  Scan, 
  Blend, 
  BarChart3, 
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  X,
  User,
  Import,
  CircleCheckBig,
  MonitorCheck,
  Shield, 
  FlaskConical,
  Glasses,
  Workflow,
  SearchCheck,
  AlertTriangle,
  Users,
  Bug,
  ClipboardList,
  Package
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Create a context for the drawer
const DrawerContext = createContext();

// Custom hook to use the drawer
export const useDrawer = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('useDrawer must be used within DrawerProvider');
  }
  return context;
};

const DrawerProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [title, setTitle] = useState('');

  const openDrawer = (title, content) => {
    setTitle(title);
    setContent(content);
    setIsOpen(true);
  };

  const closeDrawer = () => {
    setIsOpen(false);
    setTimeout(() => {
      setContent(null);
      setTitle('');
    }, 300);
  };

  return (
    <DrawerContext.Provider value={{ openDrawer, closeDrawer, isOpen }}>
      {children}
      
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-500 z-40 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeDrawer}
      />
      
      <div className={`fixed top-0 right-0 h-full bg-white shadow-2xl transition-transform duration-300 ease-in-out z-50 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } w-150`}>
        <div className="relative h-14 bg-gray-50 border-b">
          <button
            onClick={closeDrawer}
            className="absolute top-2 right-2 p-2 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label="Close drawer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">{title}</h2>
        </div>
        
        <div className="p-6 overflow-y-auto h-[calc(100%-140px)]">
          {content}
        </div>
      </div>
    </DrawerContext.Provider>
  );
};

// Reusable Sidebar Menu Component with Role-Based Filtering
const SidebarMenu = React.memo(({ 
  menuStructure, 
  currentPath, 
  onNavigate, 
  logoText = 'L',
  logoLabel = 'Dashboard',
  isExpanded,
  onToggleExpanded,
  user,
  onLogout,
  projectId
}) => {
  // Track which sections are collapsed (default: all expanded)
  const [collapsedSections, setCollapsedSections] = useState({});

  const toggleSection = (sectionIndex) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex]
    }));
  };

  const getDisplayName = () => {
    if (user?.first_name) {
      return user.first_name;
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  const displayName = getDisplayName();
  
  // Filter menu items based on user roles (exact match, no hierarchy)
  const filteredMenuStructure = useMemo(() => {
    // Don't filter if no user
    if (!user || !user.projects || !projectId) {
      return menuStructure;
    }

    // Find user's role in the current project
    const project = user.projects.find(p => 
      p.id === projectId || p.slug === projectId
    );
    
    if (!project || !project.user_role) {
      return menuStructure;
    }
    
    const userRole = project.user_role;

    return menuStructure.map(section => ({
      ...section,
      items: section.items.filter(item => {
        // If no allowedRoles specified, show to everyone
        if (!item.allowedRoles || item.allowedRoles.length === 0) {
          return true;
        }
        
        // Check if user's exact role is in the allowed roles list
        return item.allowedRoles.includes(userRole);
      })
    })).filter(section => section.items.length > 0); // Remove empty sections
  }, [menuStructure, user, projectId]);
  
  // Memoize to prevent recalculation on every render
  const activeMenuItem = useMemo(() => {
    const allMenuItems = filteredMenuStructure.flatMap(section => section.items);
    return allMenuItems.find(item => item.path === currentPath);
  }, [filteredMenuStructure, currentPath]);

  return (
    <div 
      className={`bg-gray-900 text-white transition-all duration-300 ease-in-out ${
        isExpanded ? 'w-64' : 'w-20'
      } relative overflow-hidden flex flex-col`}
    >
      {/* Fixed Header */}
      <div className="p-4 flex-shrink-0 relative z-10 bg-gray-900">
        {/* Toggle Button */}
        <button
          onClick={onToggleExpanded}
          className="w-12 h-12 p-2 mb-4 rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center"
        >
          {isExpanded ? (
            <ChevronLeft className="w-5 h-5" />
          ) : (
            <ChevronRight className="w-5 h-5" />
          )}
        </button>
        
        {/* Logo */}
        <div className="flex items-center mb-8">
          <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold">{logoText}</span>
          </div>
          <span className={`ml-3 text-xl font-semibold whitespace-nowrap transition-all duration-300 ${
            isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
          }`}>
            {logoLabel}
          </span>
        </div>
        
        {/* Top Fade Overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-b from-transparent to-gray-900 pointer-events-none z-20"></div>
      </div>

      {/* Scrollable Menu */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 scrollbar-hide">
        <nav>
          {filteredMenuStructure.map((section, sectionIndex) => {
            const isSectionCollapsed = collapsedSections[sectionIndex];
            // Check if any item in this section is active
            const hasActiveItem = section.items.some(item => activeMenuItem?.id === item.id);
            
            return (
              <div key={sectionIndex}>
                {/* Section Title/Divider */}
                {sectionIndex >= 0 && (
                  <div className="relative my-4">
                    <div className={`border-t transition-colors ${
                      hasActiveItem && isSectionCollapsed ? 'border-blue-500' : 'border-gray-700'
                    }`}></div>
                    {section.title && (
                      <div 
                        className={`absolute top-1/2 left-0 transform -translate-y-1/2 transition-all duration-300 ${
                          isExpanded ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <button
                          onClick={() => toggleSection(sectionIndex)}
                          className={`ml-4 px-3 bg-gray-900 text-xs font-semibold uppercase tracking-wider hover:text-gray-300 transition-colors flex items-center gap-1 ${
                            hasActiveItem && isSectionCollapsed ? 'text-blue-400' : 'text-gray-400'
                          }`}
                        >
                          {section.title}
                          {isSectionCollapsed ? (
                            <ChevronDown className="w-3 h-3" />
                          ) : (
                            <ChevronUp className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    )}
                    {/* Collapsed indicator when sidebar is compressed */}
                    {!isExpanded && section.title && (
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <button
                          onClick={() => toggleSection(sectionIndex)}
                          className={`w-6 h-6 flex items-center justify-center rounded transition-colors ${
                            hasActiveItem && isSectionCollapsed
                              ? 'bg-blue-600 hover:bg-blue-700'
                              : 'bg-gray-800 hover:bg-gray-700'
                          }`}
                          title={section.title}
                        >
                          {isSectionCollapsed ? (
                            <ChevronDown className={`w-3 h-3 ${
                              hasActiveItem ? 'text-white' : 'text-gray-400'
                            }`} />
                          ) : (
                            <ChevronUp className={`w-3 h-3 ${
                              hasActiveItem ? 'text-white' : 'text-gray-400'
                            }`} />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Menu Items - with collapse animation */}
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isSectionCollapsed ? 'max-h-0 opacity-0' : 'max-h-[1000px] opacity-100'
                  }`}
                >
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeMenuItem?.id === item.id;
                    
                    return (
                      <button
                        key={item.id}
                        onClick={() => onNavigate(item.path)}
                        className={`w-full flex items-center px-3 py-3 mb-2 rounded-lg transition-all duration-200 ${
                          isActive 
                            ? 'bg-blue-600 text-white' 
                            : 'hover:bg-gray-800 text-gray-300 hover:text-white'
                        }`}
                        title={!isExpanded ? item.label : ''}
                      >
                        <Icon className="w-6 h-6 flex-shrink-0" />
                        <span className={`ml-3 whitespace-nowrap transition-all duration-300 ${
                          isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                        }`}>
                          {item.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Fixed User Profile Section */}
      <div className="border-t border-gray-700 p-4 flex-shrink-0 relative z-10 bg-gray-900">
        {/* Bottom Fade Overlay */}
        <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-t from-transparent to-gray-900 pointer-events-none z-20"></div>
        
        <div className="flex items-center">
          <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
            {user?.first_name ? (
              <span className="text-sm font-semibold text-white">
                {user.first_name.charAt(0).toUpperCase()}
              </span>
            ) : (
              <User className="w-5 h-5 text-gray-400" />
            )}
          </div>
          <div className={`ml-3 flex-1 transition-all duration-300 overflow-hidden ${
            isExpanded ? 'opacity-100 translate-x-0 w-auto' : 'opacity-0 -translate-x-4 w-0'
          }`}>
            <p className="text-sm font-medium text-white truncate">
              {displayName}
            </p>
            <p className="text-xs text-gray-400 truncate">
              {user?.email}
            </p>
          </div>
          {isExpanded && (
            <button
              onClick={onLogout}
              className="ml-2 px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 rounded transition-colors"
              title="Logout"
            >
              Logout
            </button>
          )}
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
});

SidebarMenu.displayName = 'SidebarMenu';

// Layout Component with Role-Based Access Control
const LuxotticaLayout = ({ children }) => {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // ✅ Use auth hook to get user and logout
  const { user, logout } = useAuth();

  // Extract projectId from current path - FIXED to always return 'luxottica'
  const projectId = useMemo(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    // Always return 'luxottica' as the project ID for this layout
    if (pathParts.length > 0 && pathParts[0] === 'luxottica') {
      return 'luxottica';
    }
    return 'luxottica'; // Default fallback
  }, [location.pathname]);

  // Menu structure with role-based access control
  // Each item has an 'allowedRoles' array that specifies which roles can see it

  const menuStructure = useMemo(() => [
    {
      type: 'section',
      title: 'Main',
      items: [
        { 
          id: 'dashboard', 
          icon: Home, 
          label: 'Dashboard',
          path: '/dashboard',
          allowedRoles: ['user', 'viewer', 'reviewer', 'uploader', 'manager', 'owner', 'admin', 'client'] // Everyone
        },
        { 
          id: 'system-status', 
          icon: MonitorCheck, 
          label: 'System Status',
          path: '/system-status',
          allowedRoles: ['admin', 'owner', 'manager'] // Admin and owner only
        },
        { 
          id: 'reviewers', 
          icon: Users, 
          label: 'Reviewers',
          path: '/users',
          allowedRoles: ['admin'] // exposes every reviewer's earnings; grants quota; settles payouts
        },
      ]
    },
    {
      type: 'section',
      title: 'Workflows',
      items: [
        { 
          id: 'workflow-template', 
          icon: Workflow, 
          label: 'Template',
          path: '/workflows-template',
          allowedRoles: ['owner', 'admin'] // Owner and admin only
        },
        { 
          id: 'workflow-analytics', 
          icon: BarChart3, 
          label: 'Analytics',
          path: '/workflows-analytics',
          allowedRoles: ['viewer', 'reviewer', 'manager', 'owner', 'admin'] // Viewer and above
        },
        { 
          id: 'workflow-debug', 
          icon: Bug, 
          label: 'Debug',
          path: '/workflows-debug',
          allowedRoles: ['manager', 'owner', 'admin'] // resets pipeline state
        }
      ]
    },
    {
      type: 'section',
      title: 'Ingest',
      items: [
        { 
          id: 'data-ingest', 
          icon: Import, 
          label: 'Ingest',
          path: '/data/ingest',
          allowedRoles: ['uploader', 'owner', 'admin', 'client'] // Uploader and above
        }
      ]
    },
    {
      type: 'section',
      title: 'Lenses',
      items: [
        { 
          id: 'lens_bbox_verification', 
          icon: Scan,
          label: 'BBox Verification',
          path: '/lensrec',
          allowedRoles: ['admin'] // Reviewer and above
        },
        { 
          id: 'segmentation_masks_verification', 
          icon: Blend, 
          label: 'Mask Verification',
          path: '/segmentation',
          allowedRoles: ['reviewer', 'manager', 'owner', 'admin'] // Reviewer and above
        }
      ]
    },
    {
      type: 'section',
      title: 'Glasses',
      items: [
        { 
          id: 'glasses-frames', 
          icon: Glasses, 
          label: 'All Frames',
          path: '/glasses',
          allowedRoles: ['viewer', 'reviewer', 'manager', 'owner', 'admin'] // Viewer and above
        },
        { 
          id: 'glasses-frames-revision', 
          icon: SearchCheck, 
          label: 'Frames Revision',
          path: '/revision/glasses-frames',
          allowedRoles: ['viewer', 'reviewer', 'manager', 'owner', 'admin'] // Viewer and above
        }
      ]
    },
    {
      type: 'section',
      title: 'Work Records',
      items: [
        { 
          id: 'work-record-requests', 
          icon: ClipboardList, 
          label: 'Work Requests',
          path: '/work-record',
          allowedRoles: ['owner', 'admin'] // Reviewer and above
        },
        { 
          id: 'work-batches', 
          icon: Package, 
          label: 'Work Batches',
          path: '/work-batches',
          allowedRoles: ['owner', 'admin']
        }
      ]
    },
    {
      type: 'section',
      title: 'Review',
      items: [
        { 
          id: 'final-review', 
          icon: CircleCheckBig, 
          label: 'Final Review',
          path: '/review/final',
          allowedRoles: ['reviewer', 'manager', 'owner', 'admin'] // Reviewer and above
        },
        { 
          id: 'admin-review', 
          icon: Shield, 
          label: 'Admin Review',
          path: '/review/admin',
          allowedRoles: ['owner', 'admin'] // Admin and owner only
        }
      ]
    },

  ], []); // Empty dependency array - only create once



  const currentPath = location.pathname;

  const navigateTo = (path) => {
    navigate(path);
  };

  // ✅ Create stable logout handler
  const handleLogout = () => {
    logout(projectId);
  };

  return (
    <DrawerProvider>
      <div className="flex h-screen bg-gray-100">
        <SidebarMenu
          menuStructure={menuStructure}
          currentPath={currentPath}
          onNavigate={navigateTo}
          logoText="L"
          logoLabel="Lux Dashboard"
          isExpanded={isSidebarExpanded}
          onToggleExpanded={() => setIsSidebarExpanded(!isSidebarExpanded)}
          user={user}
          onLogout={handleLogout}
          projectId={projectId}
        />

        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
    </DrawerProvider>
  );
};

// Main Dashboard Component - for backwards compatibility
const LuxotticaDashboard = () => {
  return (
    <LuxotticaLayout>
      {/* Dashboard content */}
    </LuxotticaLayout>
  );
};

export default LuxotticaDashboard;
export { SidebarMenu, DrawerProvider, LuxotticaLayout };